import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import PhotoSlideshow from './PhotoSlideshow.jsx'

// TEST/premium: deep pine wall + gold title; club/live keep the black wall.
const WALL_BG = PBCC_PREMIUM ? 'var(--tv-bg-green, #0E281D)' : '#000'
const WALL_GOLD = PBCC_PREMIUM ? '#C2A24E' : CLUB.accentColor
import { approvedPhotos } from '../lib/photoCuration.js'

// Run EVERY approved photo, newest-first — no cap, nothing dropped. Set
// MAX_ON_WALL to a finite number to bring back a rolling newest-N cap. (Was 100;
// before that a 90-min rolling time window + 40-photo backfill.)
const MAX_ON_WALL = Infinity

// Passive TV photo wall — the auto-advancing slideshow with NO tab chrome.
//   /tv/photos, /tv/split (TvScreen kind='photos'), the admin monitor tile,
//   and /slideshow (the phone "Play" full-screen).
// The interactive, tabbed browse lives in PhotoBrowse.jsx (mounted at
// /photowall). Both read the same approved photos; ordering + labels come from
// PhotoSlideshow (buildTvRotation).
//
//   tournamentIdProp — pin without a URL param (TV auto-pick / admin monitor).
//   embedded         — fit a parent-sized container instead of the viewport.
export default function PhotoWallApp({ tournamentIdProp = null, embedded = false } = {}) {
  const [params] = useSearchParams()
  const tournamentId = tournamentIdProp ?? params.get('t')

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the photo wall QR.')
      return
    }
    const off = onValue(ref(db, `tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  // CHRONOLOGICAL playback: the wall runs oldest → newest, then loops back to
  // oldest. approvedPhotos() is newest-first, so we cap to the newest N (if
  // MAX_ON_WALL is finite) FIRST, then reverse to oldest-first so playback runs
  // forward in time. Currently uncapped → every approved photo, oldest → newest.
  const photos = useMemo(() => {
    const all = approvedPhotos(tournament?.photos)                                  // newest-first
    const capped = Number.isFinite(MAX_ON_WALL) ? all.slice(0, MAX_ON_WALL) : all   // newest N (or all)
    return capped.slice().reverse()                                                 // oldest-first → forward in time
  }, [tournament])

  const wrapperClass = embedded ? 'w-full h-full relative overflow-hidden rounded-xl' : 'min-h-full'
  return (
    <div className={wrapperClass} style={{ backgroundColor: WALL_BG, fontFamily: 'Marcellus, Georgia, serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
      {error ? (
        <FullScreenMessage embedded={embedded}>
          <div className="text-3xl mb-2">⚠️</div>
          <p>{error}</p>
        </FullScreenMessage>
      ) : !tournament ? (
        <FullScreenMessage embedded={embedded}><p>Loading…</p></FullScreenMessage>
      ) : photos.length === 0 ? (
        <EmptyWall embedded={embedded} />
      ) : (
        <PhotoSlideshow
          photos={photos}
          tournament={tournament}
          tournamentId={tournamentId}
          videoUnmuted={!!tournament?.photoSettings?.videoUnmuted}
          embedded={embedded}
        />
      )}
    </div>
  )
}

function FullScreenMessage({ children, embedded }) {
  return (
    <div className={`${embedded ? 'h-full' : 'min-h-screen'} flex items-center justify-center text-white text-center p-12`}>
      <div>{children}</div>
    </div>
  )
}

function EmptyWall({ embedded }) {
  return (
    <div className={`${embedded ? 'h-full' : 'min-h-screen'} flex flex-col items-center justify-center text-center p-6 text-white`}>
      <div className="p-6 mb-6">
        <img src={CLUB.logoLight} alt={CLUB.name} style={{ height: '160px', objectFit: 'contain' }} />
      </div>
      <h1 className="text-3xl font-extrabold tracking-widest uppercase" style={{ color: WALL_GOLD }}>
        📸 Photo Wall
      </h1>
      <p className="text-white/60 mt-8 text-base max-w-lg">
        Photos will appear here as they are approved during the tournament.
      </p>
    </div>
  )
}
